/**
 * Branded string type for entity identity.
 *
 * Two namespaces coexist:
 *   - Blend objects: "b:<blender object name>"  (deterministic from name)
 *   - Scene objects: the existing id as-is       (always starts with "obj_")
 *
 * The prefixes are disjoint, so collisions are impossible.
 */

/** Opaque entity identifier. */
export type EntityId = string & { readonly __brand: "EntityId" };

const BLEND_PREFIX = "b:";

/** Create an EntityId for a blend-file object from its Blender name. */
export function blendEntityId(name: string): EntityId {
  return (BLEND_PREFIX + name) as EntityId;
}

/** Create an EntityId for a user-created scene object from its existing id. */
export function sceneEntityId(id: string): EntityId {
  return id as EntityId;
}

/** True if this EntityId originated from a blend-file object. */
export function isBlendEntityId(id: EntityId): boolean {
  return id.startsWith(BLEND_PREFIX);
}

/** Extract the original Blender object name from a blend EntityId. */
export function blendNameFromId(id: EntityId): string {
  return id.slice(BLEND_PREFIX.length);
}

/**
 * Convert a store key (blend object name or scene object id) to an EntityId.
 *
 * Keys starting with "obj_" are scene objects; all others are blend
 * object names. Safe because Blender names never start with "obj_".
 */
export function storeKeyToEntityId(key: string): EntityId {
  if (key.startsWith("obj_")) return sceneEntityId(key);
  return blendEntityId(key);
}

/**
 * Convert an EntityId back to the store key format used by
 * selectedObjects, visibility, collections, etc.
 */
export function entityIdToStoreKey(id: EntityId): string {
  if (isBlendEntityId(id)) return blendNameFromId(id);
  return id;
}
